import { find, isUndefined } from 'lodash'

import { followingProspectId, useFollow, useUnfollow } from 'common/follow-college-service'
import { useMetadataToTrackedEvent } from 'common/metadata-custom-hooks'
import { useRedirectToSignIn } from 'common/use-redirect-to-signin'
import { useIsAuthed } from 'components/AuthShield'
import { useRelevantCycle } from 'store/application-cycle/hooks'
import { Prospect } from 'store/prospects/type-definitions'

interface ProspectGroups {
  following?: Prospect[]
  applying?: Prospect[]
  applied?: Prospect[]
}

export const useIsFollowing = (scid: number, prospects?: ProspectGroups) => {
  return !isUndefined(followingProspectId(scid, prospects))
}

export const useIsApplyingOrApplied = (scid: number, prospects: ProspectGroups = {}, ignoreAppCycle: boolean) => {
  const appCycle = useRelevantCycle()
  const { applying, applied } = prospects
  const isFollowing = useIsFollowing(scid, prospects)
  let isApplyingOrApplied = false
  if (!isUndefined(applying) && !isUndefined(applied)) {
    isApplyingOrApplied = !isUndefined(
      find(
        [...applying, ...applied],
        ({ CollegeScid, ApplicationCycle }) => CollegeScid === scid && (ignoreAppCycle || ApplicationCycle >= appCycle),
      ),
    )
  }
  return !isFollowing && isApplyingOrApplied
}
export const useIsOldApplyingOrApplied = (scid: number, prospects: ProspectGroups = {}, ignoreAppCycle: boolean) => {
  const appCycle = useRelevantCycle()
  const { applying, applied } = prospects
  let isApplyingOrApplied = false
  if (!isUndefined(applying) && !isUndefined(applied)) {
    isApplyingOrApplied = !isUndefined(
      find(
        [...applying, ...applied],
        ({ CollegeScid, ApplicationCycle }) => CollegeScid === scid && (ignoreAppCycle || ApplicationCycle >= appCycle),
      ),
    )
  }
  return isApplyingOrApplied
}

export const useIsFollowingState = (scid: number, prospects?: ProspectGroups) => {
  const isFollowing = useIsFollowing(scid, prospects)
  const isApplyingOrApplied = useIsApplyingOrApplied(scid, prospects, false)
  return isFollowing || isApplyingOrApplied
}

export const useFollowToggleText = (scid: number, prospects?: ProspectGroups) => {
  const isAuthed = useIsAuthed()

  const isApplyingOrApplied = useIsApplyingOrApplied(scid, prospects, false)
  const followText = !useIsFollowingState(scid, prospects)
    ? 'Add to My Colleges'
    : isApplyingOrApplied
      ? 'Already Applying'
      : 'Remove from My Colleges'

  if (!isAuthed) return 'Sign in to add this college to your list'

  return followText
}

export const useFollowToggle = ({
  scid,
  prospects,
  onToggle,
  onAddSnack,
  setSaveFollowState,
}: {
  scid: number
  prospects?: ProspectGroups
  onToggle: () => void
  onAddSnack: (message: string) => void
  setSaveFollowState?: (state: boolean) => void
}) => {
  const followEventDataRetriever = useMetadataToTrackedEvent('Follow College')
  const dismissEventDataRetriever = useMetadataToTrackedEvent('Dismissed College')

  const isFollowing = useIsFollowing(scid, prospects)
  const isApplyingOrApplied = useIsApplyingOrApplied(scid, prospects, false)

  const unfollow = useUnfollow({ scid, prospects })
  const follow = useFollow({ scid })
  const isAuthed = useIsAuthed()
  const redirectToSignIn = useRedirectToSignIn()

  return async () => {
    if (!isAuthed) return redirectToSignIn()

    if (isApplyingOrApplied) {
      onAddSnack('Cannot follow a college you already are applying to.')
    } else {
      const promise = isFollowing
        ? unfollow(dismissEventDataRetriever({ flat: true }))
        : follow(followEventDataRetriever({ flat: true }))
      await promise
        .then(() => onToggle())
        .catch(() => {
          if (setSaveFollowState) setSaveFollowState(false)
          const action = isFollowing ? 'unfollow' : 'follow'
          return onAddSnack(`Failed to ${action} college`)
        })
    }
  }
}
